import styles from "./GameLobby.module.scss";
import {useNavigate, useParams} from "react-router-dom";
import {useCallback, useEffect, useRef, useState} from "react";
import {useLobbySocket} from "../../../../api/ws/useLobbySocket";
import {getLobby} from "../../../../api/lobby";
import {AckCode, AckEvent, LobbyEvent} from "../../../../api/ws/events";
import {ConnectionStatus, Lobby, ReadyStatus, reduceLobby} from "../../../types/lobby";
import {toInitials, toNum} from "../../../utils";
import {getCurrentUsername} from "../../../../api/authContext";

const GameLobby = () => {
    const lobbyId = toNum(useParams()["lobbyId"]);

    const navigate = useNavigate();
    const [lobby, setLobby] = useState<Lobby | null>(null);
    const lobbyVersionRef = useRef<number>(0);
    const syncRef = useRef<() => void>(() => {
    });

    useEffect(() => {
        if (Number.isNaN(lobbyId)) {
            navigate("/lobby");
            return;
        }

        getLobby(lobbyId)
            .then(lobby => {
                setLobby(lobby);
                if (lobbyVersionRef.current < lobby.version) {
                    lobbyVersionRef.current = lobby.version;
                }

                if (lobby.state === 'IN_GAME' && lobby.activeGameId) {
                    navigate(`/game/${lobby.activeGameId}`);
                }
            })
            .catch(() => navigate("/lobby"));
    }, [lobbyId, navigate]);

    const onLobbyEvent = useCallback((event: LobbyEvent) => {
        console.log("LobbyEvent", event);

        const eventVersion = event.version;
        const currentVersion = lobbyVersionRef.current;

        if (event.type === 'LOBBY_STATE' && eventVersion >= currentVersion) {
            console.log(`Apply Lobby state event: ${currentVersion} -> ${eventVersion}`);
            setLobby(prev => reduceLobby(prev, event));

            lobbyVersionRef.current = eventVersion;

            if (event.lobby.state === 'IN_GAME' && event.lobby.activeGameId) {
                navigate(`/game/${event.lobby.activeGameId}`);
            }
            return;
        }

        if (eventVersion <= currentVersion) {
            console.log("Received old lobby event", event.version);
            return;
        }

        if (eventVersion === currentVersion + 1) {
            console.log(`Update lobby state : ${currentVersion} -> ${eventVersion}`);

            setLobby(prev => reduceLobby(prev, event));
            lobbyVersionRef.current = eventVersion;

            if (event.type === 'GAME_STARTED') {
                navigate(`/game/${event.gameId}`)
            }
            return;
        }

        console.warn('GAP in versions, need sync', {currentVersion, got: eventVersion});
        syncRef.current();
    }, []);

    const onAck = useCallback((ack: AckEvent) => {
        if (ack.type === 'ERROR') {
            console.error('[ACK ERROR]', ack.code, ack.message);

            if (ack.code === AckCode.SYNC_NEEDED) {
                syncRef.current();
            }

            return;
        }

        if (ack.type === 'OK') {
            console.log('[ACK OK]', ack.requestId);
            return;
        }

        console.log('[ACK OTHER]', ack);
    }, []);

    const {ready, start, sync, leave} = useLobbySocket({
        lobbyId: lobbyId,
        onLobbyEvent,
        onAck,
    });

    useEffect(() => {
        syncRef.current = sync;
    }, [sync]);

    const leaveLobby = () => {
        leave();
        navigate('/lobby');
    };

    const startGame = () => {
        start();
    };

    if (!lobby) {
        return <div className={styles.gameLobby__loading}>Loading lobby...</div>;
    }

    const allReady = (lobby.users.length >= 2 || lobby.capacity === lobby.users.length)
        && lobby.users.every(user => user.lobbyUserData.readyStatus === ReadyStatus.READY
            && user.lobbyUserData.connectionStatus === ConnectionStatus.ONLINE);

    return (
        <div className={styles.gameLobby}>
            <div className={styles.gameLobby__header}>
                <div className={styles.gameLobby__info}>
                    <h1 className={styles.gameLobby__title}>{lobby.title}</h1>
                    <div className={styles.gameLobby__meta}>
                        <span>Games Played: {lobby.gamesPlayed}</span>
                        <span>
                            Players: {lobby.users.length} / {lobby.capacity}
                        </span>
                        {lobby.capacity === 1 && (
                            <span>Solo game, no stats recording</span>
                        )}
                        <span
                            className={styles[`gameLobby__state--${lobby.state?.toLowerCase?.() || "unknown"}`]}>
                            {lobby.state}
                        </span>
                    </div>
                </div>

                <div className={styles.gameLobby__controls}>
                    <div className={styles.gameLobby__readyButtons}>
                        <button
                            className={`${styles.gameLobby__btn} ${styles["gameLobby__btn--ghost"]}`}
                            onClick={leaveLobby}
                        >
                            Leave
                        </button>
                        <button
                            className={`${styles.gameLobby__btn} ${styles["gameLobby__btn--primary"]}`}
                            onClick={() => ready(true)}
                        >
                            I&apos;m ready
                        </button>
                        <button
                            className={`${styles.gameLobby__btn} ${styles["gameLobby__btn--ghost"]}`}
                            onClick={() => ready(false)}
                        >
                            Not ready
                        </button>
                    </div>

                    <button
                        disabled={!allReady}
                        className={`${styles.gameLobby__btn} ${styles["gameLobby__btn--accent"]}`}
                        onClick={startGame}
                    >
                        Start Game
                    </button>
                </div>
            </div>

            <div className={styles.gameLobby__body}>
                <section className={styles.gameLobby__playersPanel}>
                    <h2 className={styles.gameLobby__sectionTitle}>Players</h2>
                    <ul className={styles.gameLobby__playersList}>
                        {lobby.users.map(player => {
                            const username = player.user.username;
                            const isReady = player.lobbyUserData.readyStatus === ReadyStatus.READY;
                            const isConnected = player.lobbyUserData.connectionStatus === ConnectionStatus.ONLINE;

                            return (
                                <li
                                    key={username}
                                    className={styles.playerRow}
                                >
                                    <div className={styles.playerRow__left}>
                                        <div className={styles.playerRow__avatar}>
                                            {toInitials(username)}
                                        </div>
                                        <div className={styles.playerRow__name}>
                                            {username}
                                        </div>
                                        {getCurrentUsername() === username && (<span className={styles.playerRow__youTag}>You</span>)}
                                    </div>

                                    <div className={styles.playerRow__meta}>
                                        <span>
                                            Score: {player.lobbyUserData.currentScore}
                                        </span>
                                        <span>
                                            Games won: {player.lobbyUserData.gamesWon}
                                        </span>
                                        <div
                                            className={`${styles.playerRow__status} ${
                                                (isReady && isConnected)
                                                    ? styles["playerRow__status--ready"]
                                                    : styles["playerRow__status--notReady"]
                                            }`}
                                        >
                                            <span className={styles.playerRow__statusDot}/>
                                            <span className={styles.playerRow__statusText}>
                                              {!isConnected ? 'Reconnecting…' : isReady ? 'Ready' : 'Not ready'}
                                        </span>
                                        </div>
                                    </div>
                                </li>
                            );
                        })}
                    </ul>
                </section>
            </div>
        </div>
    );
};

export default GameLobby;
